'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterState {
  category: string;
  dateRange: string;
  searchTerm: string;
  sortBy: 'date' | 'popularity' | 'relevance';
}

interface Article {
  id: string;
  title: string;
  excerpt: string;
  featuredImage: {
    url: string;
    alt: string;
  };
  author: string;
  publishedAt: string;
  category: string;
  categoryColor?: string;
  readTime: number;
  tags: string[];
  isFeatured?: boolean;
  slug: string;
}

interface NewsFiltersProps {
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onClearFilters: () => void;
  resultCount: number;
  articles: Article[];
}

const NewsFilters = ({ filters, onFilterChange, onClearFilters, resultCount, articles }: NewsFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get unique categories from articles
  const categories = Array.from(new Set(articles.map(article => article.category)));

  const dateRangeOptions = [
    { value: '', label: 'All Time' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 3 Months' },
  ];

  const sortOptions = [
    { value: 'date', label: 'Newest First' },
    { value: 'popularity', label: 'Most Popular' },
    { value: 'relevance', label: 'Most Relevant' },
  ];

  const hasActiveFilters = filters.category || filters.dateRange || filters.searchTerm;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-heading font-bold text-card-foreground uppercase tracking-wide">
            Filter Articles
          </h2>
          <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-heading font-bold">
            {resultCount} Results
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="text-xs text-text-secondary hover:text-primary transition-industrial font-medium"
            >
              Clear All
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden flex items-center space-x-1 text-sm text-text-secondary hover:text-primary transition-industrial"
          >
            <span>Filters</span>
            <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} />
          </button>
        </div>
      </div>

      {/* Filter Content */}
      <div className={`${isExpanded ? 'block' : 'hidden'} lg:block`}>
        <div className="p-4 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search articles, tags, or keywords..."
              value={filters.searchTerm}
              onChange={(e) => onFilterChange({ searchTerm: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-industrial bg-background text-foreground focus:border-primary focus:outline-none"
            />
            <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-heading font-bold text-card-foreground uppercase tracking-wide mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => onFilterChange({ category: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-industrial bg-background text-foreground text-sm focus:border-primary focus:outline-none"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-heading font-bold text-card-foreground uppercase tracking-wide mb-2">
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => onFilterChange({ dateRange: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-industrial bg-background text-foreground text-sm focus:border-primary focus:outline-none"
              >
                {dateRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-heading font-bold text-card-foreground uppercase tracking-wide mb-2">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => onFilterChange({ sortBy: e.target.value as 'date' | 'popularity' | 'relevance' })}
                className="w-full px-3 py-2 border border-border rounded-industrial bg-background text-foreground text-sm focus:border-primary focus:outline-none"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
              <span className="text-xs text-text-secondary font-medium">Active filters:</span>

              {filters.category && (
                <span className="inline-flex items-center bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold">
                  {filters.category}
                  <button
                    onClick={() => onFilterChange({ category: '' })}
                    className="ml-1 hover:bg-accent rounded"
                  >
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              
              {filters.dateRange && (
                <span className="inline-flex items-center bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold">
                  {dateRangeOptions.find(opt => opt.value === filters.dateRange)?.label}
                  <button
                    onClick={() => onFilterChange({ dateRange: '' })}
                    className="ml-1 hover:bg-accent rounded"
                  >
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
              
              {filters.searchTerm && (
                <span className="inline-flex items-center bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold">
                  "{filters.searchTerm}"
                  <button
                    onClick={() => onFilterChange({ searchTerm: '' })}
                    className="ml-1 hover:bg-accent rounded"
                  >
                    <Icon name="XMarkIcon" size={12} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsFilters;